// Pure utility functions for the ConsoleDashboard module.
// None of these functions have side effects or depend on React state.

import { ConsoleDashboardDescriptionSections } from '../../../types';
import { ModelRowState } from './types';

// Generates a unique ID for model rows to track them across renders
export const makeUid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

// Minimal email-shape check: one "@" with no whitespace, and a "." in the domain part.
// Not a full RFC 5322 validator — just enough to reject obviously-not-an-email entries.
const EMAIL_SHAPE_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Parses a newline/comma/semicolon-separated string into a deduplicated array of lowercase emails
export const parseEmails = (rawValue: string) => {
  const items = rawValue
    .split(/[\n,;]/)
    .map((entry) => entry.trim().toLowerCase())
    .filter((entry) => entry.length > 0 && EMAIL_SHAPE_RE.test(entry));
  return Array.from(new Set(items));
};

// Parses a newline/comma/semicolon-separated string into a deduplicated array of trimmed strings
export const parseCsv = (rawValue: string) => {
  const items = rawValue
    .split(/[\n,;]/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
  return Array.from(new Set(items));
};

// Counts words in a string by matching runs of letters/digits/underscore.
// Unicode-aware (\p{L}/\p{N} with the "u" flag), so non-Latin scripts count
// correctly instead of matching nothing.
export const wordCount = (text: string) => (text.match(/[\p{L}\p{N}_]+/gu) || []).length;

// CHANGED: filters_applicable is now a plain string (not string[])
export const renderDescriptionFromSections = (sections: ConsoleDashboardDescriptionSections) => {
  return [
    `Dashboard Purpose: ${sections.dashboard_purpose}`,
    `What It Cannot Answer: ${sections.cannot_answer}`,
    `Filters That Can Be Applied: ${sections.filters_applicable || 'None provided.'}`,
    `What It Answers: ${sections.what_it_answers}`,
    `Business Rules Logic: ${sections.business_rules_logic}`,
  ].join('\n');
};

// CHANGED: ModelRowState now uses source_tables instead of source_table_ids
export const normalizeModelRows = (rows: ModelRowState[]): ModelRowState[] => {
  if (!rows.length) {
    return [
      {
        _uid: makeUid(),
        role: 'fact',
        view_name: '',
        grain: '',
        alias: '',
        primary_key: '',
        sql_view: '',
        source_tables: [],
      },
    ];
  }
  return rows.map((row, index) => ({
    ...row,
    role: index === 0 ? 'fact' : 'bridge',
  }));
};